import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  MenuItem,
  Divider,
  Alert,
  Snackbar,
  CircularProgress,
  Grid,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  FormControlLabel,
  Switch
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import api from '../utils/api';

// Componente per la sezione Metodi di Pagamento
const PaymentMethodsSection = () => {
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [currentMethod, setCurrentMethod] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [methodToDelete, setMethodToDelete] = useState(null);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

  const { control, handleSubmit, reset, watch, formState: { errors } } = useForm({
    defaultValues: {
      name: '',
      type: 'CASH',
      collectionCommissionPercent: '0'
    }
  });

  // Carica i metodi di pagamento
  const fetchPaymentMethods = async () => {
    setLoading(true);
    try {
      const companyId = localStorage.getItem('companyId');
      const response = await api.get('/payment-methods', {
        headers: {
          'x-company-id': companyId
        }
      });
      setPaymentMethods(response.data);
      setError(null);
    } catch (err) {
      console.error('Errore nel caricamento dei metodi di pagamento:', err);
      setError('Impossibile caricare i metodi di pagamento. Riprova più tardi.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPaymentMethods();
  }, []);

  // Apre il dialog per aggiungere/modificare un metodo di pagamento
  const handleOpenDialog = (method = null) => {
    setCurrentMethod(method);
    reset(method ? {
      name: method.name,
      type: method.type,
      collectionCommissionPercent: String(parseFloat(method.collectionCommissionPercent) || 0)
    } : { name: '', type: 'CASH', collectionCommissionPercent: '0' });
    setOpenDialog(true);
  };

  // Chiude il dialog
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentMethod(null);
  };

  // Gestisce il salvataggio di un metodo di pagamento
  const onSubmit = async (data) => {
    try {
      const companyId = localStorage.getItem('companyId');
      if (currentMethod) {
        // Modifica un metodo esistente
        await api.put(`/payment-methods/${currentMethod.id}`, data, {
          headers: {
            'x-company-id': companyId
          }
        });
        setNotification({
          open: true,
          message: 'Metodo di pagamento aggiornato con successo',
          severity: 'success'
        });
      } else {
        // Crea un nuovo metodo
        await api.post('/payment-methods', data, {
          headers: {
            'x-company-id': companyId
          }
        });
        setNotification({
          open: true,
          message: 'Metodo di pagamento creato con successo',
          severity: 'success'
        });
      }
      handleCloseDialog();
      fetchPaymentMethods();
    } catch (err) {
      console.error('Errore durante il salvataggio del metodo di pagamento:', err);
      setNotification({
        open: true,
        message: 'Errore durante il salvataggio. Riprova più tardi.',
        severity: 'error'
      });
    }
  };

  // Apre il dialog di conferma eliminazione
  const handleOpenDeleteDialog = (method) => {
    setMethodToDelete(method);
    setOpenDeleteDialog(true);
  };

  // Chiude il dialog di conferma eliminazione
  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setMethodToDelete(null);
  };

  // Gestisce l'eliminazione di un metodo di pagamento
  const handleDeleteMethod = async () => {
    if (!methodToDelete) return;

    try {
      const companyId = localStorage.getItem('companyId');
      await api.delete(`/payment-methods/${methodToDelete.id}`, {
        headers: {
          'x-company-id': companyId
        }
      });
      setNotification({
        open: true,
        message: 'Metodo di pagamento eliminato con successo',
        severity: 'success'
      });
      handleCloseDeleteDialog();
      fetchPaymentMethods();
    } catch (err) {
      console.error('Errore durante l\'eliminazione del metodo di pagamento:', err);
      setNotification({
        open: true,
        message: 'Errore durante l\'eliminazione. Riprova più tardi.',
        severity: 'error'
      });
    }
  };

  // Chiude la notifica
  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  return (
    <Box component={Paper} sx={{ p: 3, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Metodi di Pagamento</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Aggiungi Metodo
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nome</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell align="right">Comm. incasso</TableCell>
                <TableCell align="right">Azioni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paymentMethods.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    Nessun metodo di pagamento trovato
                  </TableCell>
                </TableRow>
              ) : (
                paymentMethods.map((method) => (
                  <TableRow key={method.id}>
                    <TableCell>{method.name}</TableCell>
                    <TableCell>
                      {method.type === 'CASH' ? 'Contanti' : 'Banca'}
                    </TableCell>
                    <TableCell align="right">
                      {parseFloat(method.collectionCommissionPercent || 0).toFixed(2)}%
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        color="primary"
                        size="small"
                        onClick={() => handleOpenDialog(method)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        color="error"
                        size="small"
                        onClick={() => handleOpenDeleteDialog(method)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Dialog per aggiungere/modificare un metodo di pagamento */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle>
            {currentMethod ? 'Modifica Metodo di Pagamento' : 'Nuovo Metodo di Pagamento'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 1 }}>
              <Controller
                name="name"
                control={control}
                rules={{ required: 'Il nome è obbligatorio' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Nome"
                    fullWidth
                    margin="normal"
                    error={!!errors.name}
                    helperText={errors.name?.message}
                  />
                )}
              />

              <Controller
                name="type"
                control={control}
                rules={{ required: 'Il tipo è obbligatorio' }}
                render={({ field }) => (
                  <FormControl fullWidth margin="normal" error={!!errors.type}>
                    <InputLabel>Tipo</InputLabel>
                    <Select {...field} label="Tipo">
                      <MenuItem value="CASH">Contanti</MenuItem>
                      <MenuItem value="BANK">Banca</MenuItem>
                    </Select>
                    {errors.type && (
                      <FormHelperText>{errors.type.message}</FormHelperText>
                    )}
                  </FormControl>
                )}
              />

              <Controller
                name="collectionCommissionPercent"
                control={control}
                rules={{
                  min: { value: 0, message: 'Minimo 0' },
                  max: { value: 100, message: 'Massimo 100' }
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Commissione incasso (%)"
                    type="number"
                    fullWidth
                    margin="normal"
                    inputProps={{ min: 0, max: 100, step: 0.01 }}
                    error={!!errors.collectionCommissionPercent}
                    helperText={errors.collectionCommissionPercent?.message || 'Percentuale applicata automaticamente agli incassi con questo metodo (non ai pagamenti in uscita)'}
                  />
                )}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Annulla</Button>
            <Button type="submit" variant="contained" color="primary">Salva</Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Dialog di conferma eliminazione */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Conferma eliminazione</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Sei sicuro di voler eliminare il metodo di pagamento "{methodToDelete?.name}"?
            Questa azione non può essere annullata.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Annulla</Button>
          <Button onClick={handleDeleteMethod} color="error" autoFocus>
            Elimina
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar per notifiche */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// Componente per la sezione Conti Correnti
const AccountsSection = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [currentAccount, setCurrentAccount] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [accountToDelete, setAccountToDelete] = useState(null);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

  const { control, handleSubmit, reset, formState: { errors } } = useForm({
    defaultValues: {
      name: '',
      type: 'BANK',
      amountIncludesVat: true
    }
  });

  // Carica i conti
  const fetchAccounts = async () => {
    setLoading(true);
    try {
      const companyId = localStorage.getItem('companyId');
      const response = await api.get('/accounts', {
        headers: {
          'x-company-id': companyId
        }
      });
      setAccounts(response.data);
      setError(null);
    } catch (err) {
      console.error('Errore nel caricamento dei conti:', err);
      setError('Impossibile caricare i conti. Riprova più tardi.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAccounts();
  }, []);

  // Apre il dialog per aggiungere/modificare un conto
  const handleOpenDialog = (account = null) => {
    setCurrentAccount(account);
    reset(account ? {
      name: account.name,
      type: account.type,
      amountIncludesVat: account.amountIncludesVat !== false
    } : { name: '', type: 'BANK', amountIncludesVat: true });
    setOpenDialog(true);
  };

  // Chiude il dialog
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentAccount(null);
  };

  // Gestisce il salvataggio di un conto
  const onSubmit = async (data) => {
    try {
      const companyId = localStorage.getItem('companyId');
      if (currentAccount) {
        // Modifica un conto esistente
        await api.put(`/accounts/${currentAccount.id}`, data, {
          headers: {
            'x-company-id': companyId
          }
        });
        setNotification({
          open: true,
          message: 'Conto aggiornato con successo',
          severity: 'success'
        });
      } else {
        // Crea un nuovo conto
        await api.post('/accounts', data, {
          headers: {
            'x-company-id': companyId
          }
        });
        setNotification({
          open: true,
          message: 'Conto creato con successo',
          severity: 'success'
        });
      }
      handleCloseDialog();
      fetchAccounts();
    } catch (err) {
      console.error('Errore durante il salvataggio del conto:', err);
      setNotification({
        open: true,
        message: 'Errore durante il salvataggio. Riprova più tardi.',
        severity: 'error'
      });
    }
  };

  // Apre il dialog di conferma eliminazione
  const handleOpenDeleteDialog = (account) => {
    setAccountToDelete(account);
    setOpenDeleteDialog(true);
  };

  // Chiude il dialog di conferma eliminazione
  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setAccountToDelete(null);
  };

  // Gestisce l'eliminazione di un conto
  const handleDeleteAccount = async () => {
    if (!accountToDelete) return;

    try {
      const companyId = localStorage.getItem('companyId');
      await api.delete(`/accounts/${accountToDelete.id}`, {
        headers: {
          'x-company-id': companyId
        }
      });
      setNotification({
        open: true,
        message: 'Conto eliminato con successo',
        severity: 'success'
      });
      handleCloseDeleteDialog();
      fetchAccounts();
    } catch (err) {
      console.error('Errore durante l\'eliminazione del conto:', err);
      setNotification({
        open: true,
        message: 'Errore durante l\'eliminazione. Riprova più tardi.',
        severity: 'error'
      });
    }
  };

  // Chiude la notifica
  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  return (
    <Box component={Paper} sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Conti Correnti</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Aggiungi Conto
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nome</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Trattamento IVA</TableCell>
                <TableCell align="right">Azioni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {accounts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    Nessun conto trovato
                  </TableCell>
                </TableRow>
              ) : (
                accounts.map((account) => (
                  <TableRow key={account.id}>
                    <TableCell>{account.name}</TableCell>
                    <TableCell>
                      {account.type === 'CASH' ? 'Contanti' : 'Banca'}
                    </TableCell>
                    <TableCell>
                      {account.amountIncludesVat === false ? 'Importi imponibili (IVA esclusa)' : 'Importi con IVA inclusa'}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        color="primary"
                        size="small"
                        onClick={() => handleOpenDialog(account)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        color="error"
                        size="small"
                        onClick={() => handleOpenDeleteDialog(account)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Dialog per aggiungere/modificare un conto */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle>
            {currentAccount ? 'Modifica Conto' : 'Nuovo Conto'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 1 }}>
              <Controller
                name="name"
                control={control}
                rules={{ required: 'Il nome è obbligatorio' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Nome"
                    fullWidth
                    margin="normal"
                    error={!!errors.name}
                    helperText={errors.name?.message}
                  />
                )}
              />

              <Controller
                name="type"
                control={control}
                rules={{ required: 'Il tipo è obbligatorio' }}
                render={({ field }) => (
                  <FormControl fullWidth margin="normal" error={!!errors.type}>
                    <InputLabel>Tipo</InputLabel>
                    <Select {...field} label="Tipo">
                      <MenuItem value="CASH">Contanti</MenuItem>
                      <MenuItem value="BANK">Banca</MenuItem>
                    </Select>
                    {errors.type && (
                      <FormHelperText>{errors.type.message}</FormHelperText>
                    )}
                  </FormControl>
                )}
              />

              <Controller
                name="amountIncludesVat"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    sx={{ mt: 1, display: 'block' }}
                    control={
                      <Switch
                        checked={field.value !== false}
                        onChange={(e) => field.onChange(e.target.checked)}
                      />
                    }
                    label="Importi con IVA inclusa"
                  />
                )}
              />
              <FormHelperText sx={{ mt: -1, mb: 1 }}>
                Disattiva per conti IVA esenti o dove registri l&apos;imponibile (es. cassa contanti).
              </FormHelperText>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Annulla</Button>
            <Button type="submit" variant="contained" color="primary">Salva</Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Dialog di conferma eliminazione */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Conferma eliminazione</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Sei sicuro di voler eliminare il conto "{accountToDelete?.name}"?
            Questa azione non può essere annullata.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Annulla</Button>
          <Button onClick={handleDeleteAccount} color="error" autoFocus>
            Elimina
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar per notifiche */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// Componente principale
const AccountingSettingsPage = () => {
  return (
    <>
      <Typography variant="h4" gutterBottom>Impostazioni Contabilità</Typography>
      <PaymentMethodsSection />
      <AccountsSection />
    </>
  );
};

export default AccountingSettingsPage; 